import type { AppPlanId } from './appPlans';
import type { AppWorkflow } from './appWorkflowStore';

// Internal weights mirror backend/src/domain/scanCredits.ts. Only cadence is rendered to users.

export type AppUsageTier = 1 | 2 | 3;
export type AppCadencePeriod = 'day' | 'week' | 'month';

export interface AppWorkflowCadence {
  /** Whole runs per reset period; 0 means the plan cannot run the workflow once. */
  count: number;
  period: AppCadencePeriod;
}

export const USAGE_TIER_LABELS: Record<AppUsageTier, string> = {
  1: 'Light usage',
  2: 'Moderate usage',
  3: 'Heavy usage'
};

// Unknown models are conservatively treated as heavy, matching the backend.
const USAGE_TIERS: Record<string, AppUsageTier> = {
  'gpt-5-6': 1,
  'gpt-5.6-terra': 1,
  'glm-5-3': 1,
  'openrouter/z-ai/glm-5.3': 1,
  'daybreak-blue': 2,
  'claude-opus-5-5': 3,
  'gpt-daybreak-blue-latest': 2
};

const TIER_UNITS: Record<AppUsageTier, number> = { 1: 1, 2: 2, 3: 3 };
const STANDARD_WORKFLOW_UNITS = 10;

const PLAN_ALLOWANCE: Record<AppPlanId, { units: number; period: AppCadencePeriod }> = {
  none: { units: 0, period: 'month' },
  free: { units: STANDARD_WORKFLOW_UNITS * 5, period: 'month' },
  light: { units: STANDARD_WORKFLOW_UNITS * 30, period: 'month' },
  professional: { units: STANDARD_WORKFLOW_UNITS * 30 * 3, period: 'month' }
};

export const getUsageTier = (id: string): AppUsageTier => USAGE_TIERS[id] ?? 3;

/** Internal usage for one workflow execution, based on the model assigned to each phase run. */
export const getWorkflowUnits = (workflow: AppWorkflow): number => {
  return workflow.phases.reduce((total, phase) => {
    return total + Array.from({ length: phase.runs }, (_, index) => {
      const model = phase.models[index % phase.models.length];

      return TIER_UNITS[getUsageTier(model)];
    }).reduce((phaseTotal, units) => phaseTotal + units, 0);
  }, 0);
};

/** Whole workflow runs available in the plan's actual reset period. */
export const getWorkflowCadence = (workflow: AppWorkflow, planId: AppPlanId): AppWorkflowCadence => {
  const calculatedUnits = Math.max(getWorkflowUnits(workflow), 1);
  const billableUnits = planId === 'free'
    ? Math.max(STANDARD_WORKFLOW_UNITS, calculatedUnits)
    : calculatedUnits;
  const allowance = PLAN_ALLOWANCE[planId];

  return {
    count: Math.floor(allowance.units / billableUnits),
    period: allowance.period
  };
};

export const formatCadence = ({ count, period }: AppWorkflowCadence): string => {
  return count === 0 ? 'Over plan limit' : `${count}× a ${period}`;
};
